import React from 'react';
import {Controller, useForm} from 'react-hook-form';
import classNames from 'classnames';
import PhoneInput from 'react-phone-number-input';
import {Label} from 'common/components/Label';
import {useMutation} from 'react-query';
import {signInCustomerRequest} from 'api/customer.api';
import {Icon} from 'shared/icon/icon.component';
import {useRecaptcha} from 'common/hooks';
import * as Sentry from '@sentry/react';
// import {CountryCode} from 'utils/types';
// import {parsePhoneNumber} from 'react-phone-number-input';

interface SendVerificationCodeFormFields {
  phone: string;
}

interface SendVerificationCodeFormProps extends Omit<React.HTMLProps<HTMLFormElement>, 'onSubmit' | 'onError'> {
  onSubmitted?: (formData: Omit<SendVerificationCodeFormFields, 'recaptchaToken'>) => void;
  onError?: (error: {error?: {message: string}; response?: Response} | null) => void;
  defaultValues?: Pick<SendVerificationCodeFormFields, 'phone'>;
  showError?: boolean;
}

export const SendVerificationCodeForm = React.forwardRef<HTMLFormElement, SendVerificationCodeFormProps>(
  ({onSubmitted, onError, defaultValues, showError = true, ...formProps}, ref) => {
    const {formState, handleSubmit, control, reset} = useForm<SendVerificationCodeFormFields>({
      defaultValues,
    });

    const {executeRecaptcha} = useRecaptcha();

    const sendVerificationCodeMutation = useMutation<
      Record<string, never>,
      {error?: {message: string}; response?: Response},
      {phone: string}
    >({
      mutationFn: async (body) => {
        let recaptchaToken: string;

        try {
          recaptchaToken = await executeRecaptcha('send_verification_code');
        } catch (recaptchaError) {
          // The reCAPTCHA token could not be generated client-side (script blocked, slow/offline
          // network, unsupported browser). The request never reaches the backend, so this is the
          // only place we can observe it. Capture with the phone number so it is searchable.
          Sentry.captureException(recaptchaError, {
            tags: {flow: 'send_verification_code', stage: 'client_recaptcha'},
            extra: {phone: body.phone},
          });
          throw recaptchaError;
        }

        return signInCustomerRequest({...body, 'g-recaptcha-response': recaptchaToken});
      },
      mutationKey: 'send-verification-code',
      onSuccess: (_, requestPayload) => {
        onError?.(null);
        onSubmitted?.(requestPayload);
      },
      onError: (error, requestPayload) => {
        console.error(error);
        // Capture every mutation failure (backend 4xx/5xx, network errors) with the phone number and
        // HTTP status so it is searchable alongside the client-side reCAPTCHA captures above.
        Sentry.captureException(error?.error ?? error, {
          tags: {flow: 'send_verification_code', stage: 'mutation', status: error?.response?.status ?? 'no_response'},
          extra: {phone: requestPayload?.phone, message: error?.error?.message},
        });
        onError?.(error);
      },
    });

    const onSubmit = (formData: SendVerificationCodeFormFields) => sendVerificationCodeMutation.mutate({phone: formData.phone});

    const onReset = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      reset({phone: ''});
    };

    // const localStorage = JSON.parse(window.localStorage.getItem('APP_STATE') ?? '');
    // const companyName = localStorage?.location?.section?.store?.company?.name;

    // const isRestrictedCompany = companyName === 'LDHG';
    // const allowedCountries = isRestrictedCompany ? ['US', 'CA'] : undefined;

    return (
      <form ref={ref} onReset={onReset} onSubmit={handleSubmit(onSubmit)} {...formProps}>
        <div className={classNames('kl-input-group', {'kl-input-group-danger': formState.errors.phone})}>
          <Label htmlFor="phone" className="tw-sr-only">
            Phone number
          </Label>
          <div className="tw-mt-1">
            <Controller
              name="phone"
              control={control}
              // rules={{
              //   validate: (value) => {
              //     const phoneNumber = parsePhoneNumber(value);
              //     const phoneCountry = phoneNumber?.country;
              //     if (phoneCountry) {
              //       // if (isRestrictedCompany && !allowedCountries?.includes(phoneCountry)) {
              //       //   return 'Invalid phone number.';
              //       // }
              //       return true;
              //     }
              //   },
              //   required: 'Phone number is required.',
              // }}
              render={({field: {onChange, value}}) => (
                <PhoneInput
                  placeholder="Type in your phone number"
                  name="phone"
                  value={value}
                  onChange={onChange}
                  defaultCountry="US"
                  // addInternationalOption={!isRestrictedCompany}
                  // countries={allowedCountries as CountryCode[]}
                  // countryCallingCodeEditable={!isRestrictedCompany}
                />
              )}
            />
          </div>
          {/* {formState.errors.phone ? <p className="kl-input-helper">{formState.errors.phone.message}</p> : null} */}
          {formState.errors.phone ? <p className="kl-input-helper">Phone is required</p> : null}
        </div>

        {showError && sendVerificationCodeMutation.isError && (
          <div className="kl-callout kl-callout-danger">
            <Icon icon="error" />
            <div className="kl-callout-body">
              {sendVerificationCodeMutation.error?.response?.status === 429
                ? sendVerificationCodeMutation.error?.error?.message
                : 'An error occurred sending the verification code'}
            </div>
          </div>
        )}
      </form>
    );
  }
);

SendVerificationCodeForm.displayName = 'SendVerificationCodeForm';
